import { can, type Role } from '@kia/shared';

/**
 * The navigation model — one data-driven source of truth for every destination in the authenticated
 * shell, grouped into sections so 18 flat tabs become a scannable hierarchy. Consumed by the sidebar
 * (desktop) and the mobile drawer alike. Icons are emoji to match the app's existing visual language
 * (GlobalSearch, SmartEntryDock) with zero icon-library weight.
 */

/** Every destination in the authenticated shell — the runtime source the `View` type derives from, so the
 *  hash-route parser (useHashRoute) can validate a URL view segment without a hand-maintained second list. */
export const ALL_VIEWS = [
  'dash',
  'deals',
  'inbox',
  'customers',
  'prices',
  'sense',
  'close',
  'cash',
  'buy',
  'release',
  'ledger',
  'motor',
  'master',
  'bi',
  'ai',
  'entry',
  'help',
  'settings',
  'users',
  'audit',
] as const;

export type View = (typeof ALL_VIEWS)[number];

/** Runtime guard: is a string one of the shell's views? Used to sanitise a URL hash segment. */
export function isView(s: string): s is View {
  return (ALL_VIEWS as readonly string[]).includes(s);
}

export interface NavItem {
  key: View;
  label: string;
  icon: string;
  visible: (role: Role) => boolean;
}
export interface NavGroup {
  id: string;
  label: string; // '' → an unlabelled utility group (e.g. Help)
  items: NavItem[];
}

const always = () => true;
const admin = (role: Role) => role === 'Admin';
const canRelease = (role: Role) =>
  can(role, 'release.prepare') ||
  can(role, 'release.check') ||
  can(role, 'release.approve') ||
  can(role, 'release.security');

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    id: 'core',
    label: 'کارها',
    items: [
      { key: 'dash', label: 'داشبورد', icon: '📊', visible: (r) => can(r, 'dashboard.read') },
      { key: 'deals', label: 'قراردادها و پیشنهادها', icon: '📄', visible: always },
      { key: 'inbox', label: 'استعلام‌ها', icon: '📥', visible: always },
      { key: 'customers', label: 'مشتریان', icon: '🏢', visible: always },
    ],
  },
  {
    id: 'finance',
    label: 'مالی',
    items: [
      { key: 'prices', label: 'قیمت‌ها', icon: '🏷️', visible: always },
      { key: 'sense', label: 'آزمون بها', icon: '🧮', visible: always },
      { key: 'cash', label: 'تقویم نقدینگی', icon: '💰', visible: always },
      { key: 'close', label: 'بستن ماه', icon: '📅', visible: always },
      { key: 'ledger', label: 'دفترداری', icon: '📒', visible: (r) => can(r, 'accounting.read') },
    ],
  },
  {
    id: 'ops',
    label: 'عملیات',
    items: [
      { key: 'buy', label: 'خرید و انبار', icon: '🛒', visible: always },
      { key: 'release', label: 'ترخیص', icon: '🚚', visible: canRelease },
      { key: 'motor', label: 'طراح موتور', icon: '⚙️', visible: always },
      { key: 'master', label: 'محصولات و مواد', icon: '📦', visible: always },
    ],
  },
  {
    id: 'intel',
    label: 'هوش',
    items: [
      { key: 'bi', label: 'هوش تجاری', icon: '📈', visible: always },
      { key: 'ai', label: 'دستیار تحلیل', icon: '💬', visible: always },
      { key: 'entry', label: 'ثبت هوشمند', icon: '✨', visible: (r) => can(r, 'ai.use') },
    ],
  },
  {
    id: 'admin',
    label: 'مدیریت',
    items: [
      { key: 'users', label: 'کاربران', icon: '👥', visible: admin },
      { key: 'audit', label: 'رویدادها', icon: '🕘', visible: admin },
      { key: 'settings', label: 'تنظیمات', icon: '🔧', visible: admin },
    ],
  },
  {
    id: 'util',
    label: '',
    items: [{ key: 'help', label: 'راهنما و آموزش', icon: '❓', visible: always }],
  },
];

/** The visible groups for a role (empty groups dropped) — what the sidebar renders. */
export function navFor(role: Role): NavGroup[] {
  return NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((it) => it.visible(role)) })).filter(
    (g) => g.items.length > 0,
  );
}

/** Can this role open this view? The ONE visibility authority (the same predicate the sidebar uses),
 *  reused so a deep-link (search hit, notification) never routes to a screen the role cannot see. */
export function canViewFor(role: Role, view: View): boolean {
  for (const g of NAV_GROUPS)
    for (const it of g.items) if (it.key === view) return it.visible(role);
  return false;
}

/** The landing view for a role — the first item it can actually see. The dashboard is no longer visible to
 *  every role (RBAC), so the shell must not hard-default to it; it lands on the first permitted screen. */
export function firstViewFor(role: Role): View {
  for (const g of NAV_GROUPS) for (const it of g.items) if (it.visible(role)) return it.key;
  return 'help'; // Help is always visible — a safe last resort.
}

/**
 * The workspace each role should OPEN on — its actual daily job, not merely the first nav item. Landing a
 * Warehouse/Security user on the deals LIST (the old firstViewFor behaviour) buried their real worklist, the
 * dispatch queue; a Finance user wants the cash calendar, Purchasing wants procurement, Sales wants the
 * pipeline. Management/oversight roles keep the control-tower dashboard. Always re-checked against RBAC:
 * if the preferred view isn't visible to the role, we fall back to firstViewFor, so this can never strand a
 * user on a screen they can't open.
 */
const PREFERRED_LANDING: Partial<Record<Role, View>> = {
  Admin: 'dash', // the owner's control tower
  Editor: 'dash', // general operator — the oversight view
  Viewer: 'dash', // read-only management
  Sales: 'deals', // their pipeline of offers/contracts
  Purchasing: 'buy', // procurement + inbound
  Finance: 'cash', // the cash calendar is their daily driver
  Warehouse: 'release', // the dispatch worklist, not a deals list they can't act on
  Security: 'release', // the gate/security sign-off queue
};

export function landingFor(role: Role): View {
  const pref = PREFERRED_LANDING[role];
  return pref && canViewFor(role, pref) ? pref : firstViewFor(role);
}
